import { useMemo } from "react";
import { FieldValues, useController } from "react-hook-form";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { Select, SelectProps } from "../../form_components/Inputs";
import { renderStageOption } from "./stageSelectUtils";
import { GroupedStageOptions, StageOption } from "./types";

type StageDropdownSelectProps<T extends FieldValues> = Omit<
  SelectProps<T, StageOption | GroupedStageOptions>,
  "options" | "onChange"
> & {
  options: (StageOption | GroupedStageOptions)[];
  loading?: boolean;
  fillbackground?: boolean;
  onChange?: (value: string, option?: StageOption) => void;
  preventFormValueChange?: boolean;
};

export function StageDropdownSelect<T extends FieldValues>(props: StageDropdownSelectProps<T>) {
  const { options, loading, fillbackground, onChange, ...selectProps } = props;
  const { field } = useController(props);

  const optionCache: Record<string, StageOption> = useMemo(() => {
    // Handle both flat and grouped options
    const flatOptions =
      Array.isArray(options) && options.length > 0 && "options" in options[0]
        ? (options as GroupedStageOptions[]).flatMap((group) => group.options)
        : (options as StageOption[]);

    return flatOptions.reduce((acc, option) => ({ ...acc, [option.value]: option }), {});
  }, [options]);

  let backgroundColor = undefined;
  if (fillbackground && props.variant === "borderless") {
    const selectedOption = optionCache[field.value];
    backgroundColor = selectedOption?.color ?? "transparent";
  }

  return (
    <Select
      {...selectProps}
      options={options}
      loading={loading}
      onChange={(value) => {
        if (onChange) {
          onChange(value, optionCache[value]);
        }
      }}
      labelRender={(renderProps) =>
        renderStageOption(renderProps.label, renderProps.value, options)
      }
      optionRender={(oriOption) => renderStageOption(oriOption.label, oriOption.value, options)}
      style={{ backgroundColor }}
      suffixIcon={<UIcon icon="chevronDown" color="sysTextDefault" />}
    />
  );
}
